import React, { useState, useMemo, useRef, useEffect } from "react";
import { normalizeCompany } from "../utils/companyUtils";
import MultiSelectDropdown from "./MultiSelectDropdown";

const isTaskDone = (completed) =>
  completed === true || completed === 1 || String(completed) === "true";

const DAYS_SK = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];
const MONTHS_SK = [
  "Január", "Február", "Marec", "Apríl", "Máj", "Jún",
  "Júl", "August", "September", "Október", "November", "December",
];

function pad(n) { return String(n).padStart(2, "0"); }

function buildCalDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let startDow = firstDay.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1;
  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

function TaskCard({ task, dragTaskId, onDragEnd, onStartEdit, editingTaskId, editValues, setEditValues, onSaveEdit, onCancelEdit, companyList, taskList, onDeleteTask, onToggleDone }) {
  const isEditing = editingTaskId === task.id;
  const done = isTaskDone(task.completed);

  if (isEditing) {
    return (
      <div className="kal-task kal-task-editing" onClick={e => e.stopPropagation()}>
        <input
          className="kal-edit-input"
          list="kal-task-list"
          autoFocus
          value={editValues.text || ""}
          placeholder="Názov..."
          onChange={e => setEditValues(v => ({ ...v, text: e.target.value }))}
          onKeyDown={e => { if (e.key === "Enter") onSaveEdit(task.id); if (e.key === "Escape") onCancelEdit(); }}
        />
        <datalist id="kal-task-list">
          {(taskList || []).map(t => <option key={t} value={t} />)}
        </datalist>
        <input
          className="kal-edit-input"
          list="kal-company-list"
          value={editValues.company || ""}
          placeholder="Firma..."
          onChange={e => setEditValues(v => ({ ...v, company: e.target.value.toUpperCase() }))}
          onBlur={e => setEditValues(v => ({ ...v, company: normalizeCompany(e.target.value) }))}
          onKeyDown={e => { if (e.key === "Enter") onSaveEdit(task.id); if (e.key === "Escape") onCancelEdit(); }}
        />
        <datalist id="kal-company-list">
          {(companyList || []).map(c => <option key={c} value={c} />)}
        </datalist>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <input
            className="kal-edit-input"
            type="date"
            value={editValues.deadline || ""}
            onChange={e => setEditValues(v => ({ ...v, deadline: e.target.value }))}
            onKeyDown={e => { if (e.key === "Enter") onSaveEdit(task.id); if (e.key === "Escape") onCancelEdit(); }}
            style={{ flex: 1 }}
          />
          <input
            className="kal-edit-input"
            type="number"
            value={editValues.estimatedTime || ""}
            placeholder="min"
            onChange={e => setEditValues(v => ({ ...v, estimatedTime: e.target.value }))}
            onKeyDown={e => { if (e.key === "Enter") onSaveEdit(task.id); if (e.key === "Escape") onCancelEdit(); }}
            style={{ width: 52 }}
          />
        </div>
        <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
          <button className="kal-btn-save" onClick={() => onSaveEdit(task.id)}>✓</button>
          <button className="kal-btn-cancel" onClick={onCancelEdit}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`kal-task${done ? " done" : ""}`}
      draggable
      onDragStart={e => { dragTaskId.current = task.id; e.dataTransfer.setData("text/plain", task.id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={onDragEnd}
      onClick={() => onStartEdit(task)}
      onDoubleClick={e => e.stopPropagation()}
      title="Kliknúť pre editáciu"
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
        <input
          type="checkbox"
          checked={done}
          onChange={e => { e.stopPropagation(); onToggleDone(task.id); }}
          onClick={e => e.stopPropagation()}
          title={done ? "Označiť ako nesplnené" : "Označiť ako splnené"}
          style={{ marginTop: 3, cursor: "pointer", flexShrink: 0 }}
        />
        <div className={`kal-task-name${done ? " done" : ""}`}>{task.text}</div>
      </div>
      <div className="kal-task-meta">
        {[task.company, task.estimatedTime ? `${task.estimatedTime} min` : null]
          .filter(Boolean).join(" · ")}
        <span className="kal-edit-hint"> ✎</span>
      </div>
      <button
        className="kal-btn-delete"
        onClick={e => { e.stopPropagation(); onDeleteTask(task.id); }}
        title="Vymazať"
      >🗑</button>
    </div>
  );
}

function MonthGrid({ year, month, tasks, todayStr, dragOverKey, dragTaskId,
  setDragOverKey, onDrop, onDragEnd, editingTaskId, editValues, setEditValues,
  onStartEdit, onSaveEdit, onCancelEdit, dowTop, companyList, taskList, onAddTask, onDeleteTask, onToggleDone }) {

  const [addingCellKey, setAddingCellKey] = useState(null);
  const [newTaskText, setNewTaskText] = useState("");
  const calDays = useMemo(() => buildCalDays(year, month), [year, month]);

  const tasksByDay = useMemo(() => {
    const result = {};
    tasks.forEach(task => {
      if (!task.deadline) return;
      const d = new Date(task.deadline);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!result[day]) result[day] = [];
        result[day].push(task);
      }
    });
    return result;
  }, [tasks, year, month]);

  return (
    <div className="kal-month">
      <div className="kal-month-title">{MONTHS_SK[month]} {year}</div>
      <div className="kal-grid">
        {DAYS_SK.map(d => (
          <div key={d} className="kal-dow" style={{ top: dowTop }}>{d}</div>
        ))}
        {calDays.map((day, idx) => {
          const cellKey = day ? `${year}-${pad(month + 1)}-${pad(day)}` : null;
          const isToday = cellKey === todayStr;
          const dayTasks = day ? (tasksByDay[day] || []) : [];
          return (
            <div
              key={idx}
              className={[
                "kal-cell",
                !day ? "empty" : "",
                isToday ? "today" : "",
                cellKey && dragOverKey === cellKey ? "drag-over" : "",
              ].filter(Boolean).join(" ")}
              onDragOver={e => { if (!day) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverKey(cellKey); }}
              onDragLeave={() => setDragOverKey(null)}
              onDrop={e => onDrop(e, cellKey)}
              onDoubleClick={() => { if (!day || addingCellKey === cellKey) return; setAddingCellKey(cellKey); setNewTaskText(""); }}
            >
              {day && <div className="kal-day-num">
                {day}
                <button
                  className="kal-btn-add-task"
                  onClick={e => { e.stopPropagation(); setAddingCellKey(cellKey); setNewTaskText(""); }}
                  title="Pridať povinnosť"
                >+</button>
              </div>}
              {dayTasks.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  dragTaskId={dragTaskId}
                  onDragEnd={onDragEnd}
                  onStartEdit={onStartEdit}
                  editingTaskId={editingTaskId}
                  editValues={editValues}
                  setEditValues={setEditValues}
                  onSaveEdit={onSaveEdit}
                  onCancelEdit={onCancelEdit}
                  companyList={companyList}
                  taskList={taskList}
                  onDeleteTask={onDeleteTask}
                  onToggleDone={onToggleDone}
                />
              ))}
              {addingCellKey === cellKey && (
                <div className="kal-add-form" onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}>
                  <input
                    className="kal-edit-input"
                    list="kal-task-list"
                    autoFocus
                    placeholder="Názov povinnosti..."
                    value={newTaskText}
                    onChange={e => setNewTaskText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && newTaskText.trim()) {
                        onAddTask(cellKey, newTaskText.trim());
                        setAddingCellKey(null);
                        setNewTaskText("");
                      }
                      if (e.key === "Escape") { setAddingCellKey(null); setNewTaskText(""); }
                    }}
                  />
                  <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                    <button className="kal-btn-save" onClick={() => {
                      if (newTaskText.trim()) { onAddTask(cellKey, newTaskText.trim()); }
                      setAddingCellKey(null); setNewTaskText("");
                    }}>✓</button>
                    <button className="kal-btn-cancel" onClick={() => { setAddingCellKey(null); setNewTaskText(""); }}>✕</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PovinnostiKalendar({ tasks, setTasks, saveTasks, userPin, companyList = [], addToCompanyList, taskList = [], addToTaskList }) {
  const today = new Date();
  const dragTaskId = useRef(null);
  const stickyBarRef = useRef(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [viewDate, setViewDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [hideCompleted, setHideCompleted] = useState(true);
  const [monthCount, setMonthCount] = useState(3);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [dowTop, setDowTop] = useState(0);
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [filterTaskTexts, setFilterTaskTexts] = useState([]);

  useEffect(() => {
    if (!stickyBarRef.current) return;
    const update = () => setDowTop(stickyBarRef.current.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(stickyBarRef.current);
    return () => ro.disconnect();
  }, []);

  const allCompanies = useMemo(() => {
    const set = new Set((tasks || [])
      .filter(t => !String(t.id).includes("_hist_") && !isTaskDone(t.completed))
      .map(t => t.company || "")
      .filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "sk"));
  }, [tasks]);

  const allTaskTexts = useMemo(() => {
    const map = new Map();
    (tasks || [])
      .filter(t => !String(t.id).includes("_hist_") && !isTaskDone(t.completed))
      .forEach(t => {
        const text = (t.text || "").trim();
        if (text) map.set(text.toLowerCase(), text);
      });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, "sk"));
  }, [tasks]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));
  const goToday = () => setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));

  const months = useMemo(() => {
    return Array.from({ length: monthCount }, (_, i) => {
      const d = new Date(year, month + i, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }, [year, month, monthCount]);

  const filteredTasks = useMemo(() => {
    const active = (tasks || []).filter(t => !String(t.id).includes("_hist_"));
    return active.filter(t => {
      if (hideCompleted && isTaskDone(t.completed)) return false;
      if (filterCompanies.length > 0 && !filterCompanies.includes(t.company || "")) return false;
      if (filterTaskTexts.length > 0 && !filterTaskTexts.some(f => f.toLowerCase() === (t.text || "").trim().toLowerCase())) return false;
      return true;
    });
  }, [tasks, hideCompleted, filterCompanies, filterTaskTexts]);

  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  // 1. BEZPEČNÝ DRAG & DROP
  const handleDrop = (e, cellKey) => {
    e.preventDefault();
    setDragOverKey(null);
    if (!cellKey) return;
    const taskId = e.dataTransfer.getData("text/plain") || dragTaskId.current;
    if (!taskId) return;
    dragTaskId.current = null;

    if (setTasks) {
      setTasks(prev => {
        const updated = (prev || []).map(t =>
          t.id === taskId ? { ...t, deadline: cellKey } : t
        );
        if (saveTasks) saveTasks(userPin, updated);
        return updated;
      });
    }
  };

  const handleDragEnd = () => {
    dragTaskId.current = null;
    setDragOverKey(null);
  };

  const handleStartEdit = (task) => {
    setEditingTaskId(task.id);
    setEditValues({
      text: task.text || "",
      company: task.company || "",
      deadline: task.deadline || "",
      estimatedTime: task.estimatedTime || "",
    });
  };

  // 2. BEZPEČNÁ EDITÁCIA
  const handleSaveEdit = (taskId) => {
    if (!editValues.text?.trim()) return;
    const normalized = normalizeCompany(editValues.company);
    if (normalized && !companyList.includes(normalized)) {
      const ok = window.confirm(`Firma "${normalized}" nie je v zozname. Pridať ju?`);
      if (!ok) return;
      if (addToCompanyList) addToCompanyList(normalized);
    }
    const taskName = editValues.text?.trim();
    if (taskName && !taskList.includes(taskName)) {
      const ok = window.confirm(`Povinnosť "${taskName}" nie je v zozname. Pridať ju?`);
      if (!ok) return;
      if (addToTaskList) addToTaskList(taskName);
    }
    const finalValues = { ...editValues, company: normalized || editValues.company };

    if (setTasks) {
      setTasks(prev => {
        const updated = (prev || []).map(t =>
          t.id === taskId ? { ...t, ...finalValues } : t
        );
        if (saveTasks) saveTasks(userPin, updated);
        return updated;
      });
    }
    setEditingTaskId(null);
  };

  const handleCancelEdit = () => setEditingTaskId(null);

  // 3. BEZPEČNÉ PRIDANIE ÚLOHY V KALENDÁRI
  const handleAddTask = (cellKey, text) => {
    const newTask = {
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      text,
      company: "",
      deadline: cellKey,
      interval: "none",
      estimatedTime: "",
      subtasks: [],
      completed: false,
    };
    if (addToTaskList) addToTaskList(text);

    if (setTasks) {
      setTasks(prev => {
        const updated = [...(prev || []), newTask];
        if (saveTasks) saveTasks(userPin, updated);
        return updated;
      });
    }
  };

  // 4. BEZPEČNÉ VYMAZANIE ÚLOHY
  const handleDeleteTask = (taskId) => {
    if (!window.confirm("Vymazať túto povinnosť?")) return;

    if (setTasks) {
      setTasks(prev => {
        const updated = (prev || []).filter(t => t.id !== taskId);
        if (saveTasks) saveTasks(userPin, updated);
        return updated;
      });
    }
  };

  // 5. BEZPEČNÉ OZNAČENIE ZA VYBAVENÉ
  const handleToggleDone = (taskId) => {
    if (setTasks) {
      setTasks(prev => {
        const updated = (prev || []).map(t =>
          t.id === taskId ? { ...t, completed: !isTaskDone(t.completed) } : t
        );
        if (saveTasks) saveTasks(userPin, updated);
        return updated;
      });
    }
  };

  return (
    <div className="kal-root">
      <style>{`
        .kal-root { font-family: Arial, sans-serif; }
        .kal-sticky-bar {
          position: sticky;
          top: 0;
          z-index: 30;
          background: rgba(255,255,255,0.97);
          backdrop-filter: blur(4px);
          border-bottom: 1px solid #ccc;
          padding: 8px 0 6px 0;
        }
        .kal-controls { display: flex; align-items: center; gap: 14px; margin-bottom: 6px; flex-wrap: wrap; }
        .kal-nav { display: flex; align-items: center; gap: 8px; }
        .kal-nav button { padding: 4px 14px; font-size: 15px; cursor: pointer; }
        .kal-months-wrap { display: flex; flex-direction: column; gap: 28px; padding-top: 12px; }
        .kal-month { min-width: 0; }
        .kal-month-title {
          font-size: 15px; font-weight: bold;
          text-align: center; margin-bottom: 0;
          padding: 5px 0; background: #333; color: #fff; border-radius: 4px 4px 0 0;
        }
        .kal-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          border-left: 1px solid #999;
          border-top: 1px solid #999;
        }
        .kal-dow {
          position: sticky;
          z-index: 10;
          background: #555; color: #fff;
          text-align: center; padding: 4px 0;
          font-size: 16px; font-weight: bold;
          border-right: 1px solid #444;
          border-bottom: 1px solid #444;
        }
        .kal-cell {
          border-right: 1px solid #bbb;
          border-bottom: 1px solid #bbb;
          padding: 3px 4px;
          min-height: 80px;
          background: #fff;
          vertical-align: top;
        }
        .kal-cell.empty { background: #f4f4f4; }
        .kal-cell.today { background: #f0f8ff; }
        .kal-day-num { font-weight: bold; font-size: 16px; margin-bottom: 2px; color: #333; }
        .kal-cell.today .kal-day-num {
          display: inline-block; background: #333; color: #fff;
          border-radius: 3px; padding: 0 4px;
        }
        .kal-task {
          font-size: 15px; line-height: 1.3;
          border-left: 2px solid #555;
          padding: 3px 0 3px 5px;
          margin-bottom: 4px;
          cursor: pointer;
          user-select: none;
        }
        .kal-task:hover { background: #f5f5f5; }
        .kal-task-editing {
          border-left: 2px solid #333;
          background: #f9f9f9 !important;
          cursor: default;
          padding: 4px 5px;
        }
        .kal-cell.drag-over { outline: 2px dashed #333; background: #e8e8e8; }
        .kal-task-name { font-weight: bold; font-size: 15px; }
        .kal-task-name.done { text-decoration: line-through; color: #aaa; }
        .kal-task-meta { font-size: 13px; color: #555; margin-top: 1px; }
        .kal-edit-hint { opacity: 0.35; font-size: 12px; }
        .kal-task:hover .kal-edit-hint { opacity: 0.8; }
        .kal-edit-input {
          display: block; width: 100%; box-sizing: border-box;
          font-size: 13px; border: 1px solid #aaa; border-radius: 3px;
          padding: 2px 5px; margin-bottom: 3px;
        }
        .kal-btn-save, .kal-btn-cancel {
          font-size: 12px; padding: 2px 8px; border: 1px solid #aaa;
          border-radius: 3px; cursor: pointer;
        }
        .kal-btn-save { background: #333; color: #fff; border-color: #333; }
        .kal-btn-cancel { background: #eee; color: #333; }
        .kal-btn-delete {
          display: none;
          position: absolute; top: 2px; right: 2px;
          font-size: 10px; padding: 0 3px; line-height: 14px;
          border: none; background: transparent; cursor: pointer;
          opacity: 0.5; border-radius: 3px;
        }
        .kal-task { position: relative; }
        .kal-task:hover .kal-btn-delete { display: block; }
        .kal-task:hover .kal-btn-delete:hover { opacity: 1; background: #fee2e2; }
        .kal-btn-add-task {
          display: none;
          margin-left: 4px; font-size: 11px; font-weight: bold;
          padding: 0 4px; line-height: 14px;
          border: 1px solid #aaa; border-radius: 3px;
          cursor: pointer; background: #f0f0f0; color: #333;
          vertical-align: middle;
        }
        .kal-cell:hover .kal-btn-add-task { display: inline; }
        .kal-add-form { margin-top: 4px; padding: 4px; background: #f9f9f9; border: 1px dashed #bbb; border-radius: 4px; }
        @media print {
          .kal-sticky-bar { position: static !important; }
          .kal-controls, .kal-nav { display: none !important; }
          body * { visibility: hidden; }
          .kal-root, .kal-root * { visibility: visible; }
          .kal-root { position: absolute; top: 0; left: 0; width: 100%; }
          @page { size: A4 landscape; margin: 8mm 7mm; }
          .kal-months-wrap { gap: 16px; }
          .kal-cell { min-height: 50px; padding: 1px 2px; }
          .kal-task { font-size: 9.5pt; }
          .kal-task-name { font-size: 9.5pt; }
          .kal-task-meta { font-size: 9pt; }
          .kal-day-num { font-size: 11pt; }
          .kal-dow { position: static; font-size: 9.5pt; }
          .kal-month-title { font-size: 13pt; }
        }
      `}</style>

      <div className="kal-sticky-bar" ref={stickyBarRef}>
        <div className="kal-controls">
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={hideCompleted} onChange={e => setHideCompleted(e.target.checked)} />
            Skryť splnené
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            Mesiace:
            {[1, 2, 3].map(n => (
              <button key={n} onClick={() => setMonthCount(n)}
                style={{ padding: "3px 10px", fontWeight: monthCount === n ? "bold" : "normal",
                  background: monthCount === n ? "#333" : "#eee", color: monthCount === n ? "#fff" : "#333",
                  border: "1px solid #aaa", borderRadius: 4, cursor: "pointer", fontSize: 13 }}>
                {n}
              </button>
            ))}
          </label>
          <MultiSelectDropdown
            options={allCompanies}
            selected={filterCompanies}
            onChange={setFilterCompanies}
            placeholder="Všetky firmy"
            searchPlaceholder="Hľadať firmu..."
          />
          <MultiSelectDropdown
            options={allTaskTexts}
            selected={filterTaskTexts}
            onChange={setFilterTaskTexts}
            placeholder="Všetky povinnosti"
            searchPlaceholder="Hľadať povinnosť..."
          />
          <button onClick={() => window.print()} style={{ padding: "5px 16px", fontSize: 13, cursor: "pointer" }}>
            Tlačiť
          </button>
        </div>
        <div className="kal-nav">
          <button onClick={prevMonth}>‹</button>
          <button onClick={goToday} style={{ padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>Dnes</button>
          <button onClick={nextMonth}>›</button>
        </div>
      </div>

      <div className="kal-months-wrap">
        {months.map(({ year: y, month: m }) => (
          <MonthGrid
            key={`${y}-${m}`}
            year={y} month={m}
            tasks={filteredTasks}
            todayStr={todayStr}
            dragOverKey={dragOverKey}
            dragTaskId={dragTaskId}
            setDragOverKey={setDragOverKey}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            editingTaskId={editingTaskId}
            editValues={editValues}
            setEditValues={setEditValues}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            dowTop={dowTop}
            companyList={companyList}
            taskList={taskList}
            onAddTask={handleAddTask}
            onDeleteTask={handleDeleteTask}
            onToggleDone={handleToggleDone}
          />
        ))}
      </div>
    </div>
  );
}